// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Flex,
  Link,
  RadioFieldGroup,
  SwitchField,
  Tabs,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  AgeGateFormContainer,
  AgeGateStoreSection,
} from '@jane/business-admin/components';
import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { AgeGate } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

const FormInput = styled.input(({ theme }) => ({
  width: '100%',
  display: 'flex',
  padding: '12px 16px',
  alignItems: 'flex-start',
  gap: '16px',
  alignSelf: 'stretch',
  borderRadius: '12px',
  border: `1px solid ${theme.colors.grays.light}`,
  background: `${theme.colors.grays.white}`,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  margin: '40px auto 0',
  backgroundColor: `${theme.colors.grays.ultralight}`,
  borderRadius: `${theme.borderRadius.sm}`,
}));

const StyledTab = styled(Tabs.Tab)<{ selected: boolean }>(
  ({ theme, selected }) => ({
    boxShadow: 'none !important',
    border: selected ? `2px solid ${theme.colors.primary.main}` : 'none',
    borderRadius: `${theme.borderRadius.sm}`,
    position: 'relative' as const,

    span: {
      fontWeight: 600,
    },
  })
);

export const PremiumAgeGate = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'yes_no' | 'date_picker'>(
    'yes_no'
  );

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const defaultValues = useMemo(
    () => ({
      age_gate: {
        enabled: true,
        default: 'yes_no' as const,
        date_picker: {
          enabled: false,
          cookie_name: '',
          message: 'Please confirm your birthdate to proceed',
          store_ids: [] as number[],
        },
        yes_no: {
          enabled: true,
          cookie_name: '',
          message:
            'Are you over 21 years old or a valid medical cannabis patient?',
          store_ids: [] as number[],
          button_order: 'no_yes' as const,
        },
      },
    }),
    [bloomMenuQuery.data]
  );

  const formMethods = useForm<{ age_gate: AgeGate }>({
    defaultValues,
  });
  const {
    control,
    formState: { isDirty },
    setValue,
    getValues,
    reset,
    register,
  } = formMethods;

  const date_picker_enabled = useWatch({
    control,
    name: 'age_gate.date_picker.enabled',
  });
  const yes_no_enabled = useWatch({ control, name: 'age_gate.yes_no.enabled' });
  const age_gate_default = useWatch({ control, name: 'age_gate.default' });
  const date_picker_stores = useWatch({
    control,
    name: 'age_gate.date_picker.store_ids',
  });
  const yes_no_stores = useWatch({
    control,
    name: 'age_gate.yes_no.store_ids',
  });

  const allStoreIds = useMemo(
    () => bloomMenuQuery.data?.stores.map((store) => store.id) ?? [],
    [bloomMenuQuery.data?.stores]
  );
  const isYesNoSelected = useMemo(
    () => selectedTab === 'yes_no',
    [selectedTab]
  );
  const showStoreSection = useMemo(
    () =>
      bloomMenuQuery.data?.stores &&
      bloomMenuQuery.data.stores.length > 0 &&
      selectedTab !== age_gate_default &&
      date_picker_enabled &&
      yes_no_enabled,
    [
      age_gate_default,
      bloomMenuQuery.data,
      date_picker_enabled,
      selectedTab,
      yes_no_enabled,
    ]
  );

  useEffect(() => {
    if (bloomMenuQuery.isSuccess) {
      const ageGateConfigs =
        bloomMenuQuery.data?.theme.bloom?.age_gate || defaultValues.age_gate;

      reset(
        {
          age_gate: ageGateConfigs,
        },
        { keepDirtyValues: true }
      );
    }
  }, [
    bloomMenuQuery.data?.theme.bloom?.age_gate,
    bloomMenuQuery.isSuccess,
    reset,
    setValue,
    selectedTab,
    defaultValues,
  ]);

  /* We want to make passive changes to the age gate model in the following cases:
   * 1) If both age gate types are disabled:
   *    - disable age gate
   *    - clear store ids for both age gate types
   * 2) If only one age gate type is enabled:
   *    - set that type as the default
   *    - assign all store ids to it
   *    - clear store ids in inactive type if any
   */
  useEffect(() => {
    if (isDirty) {
      if (!date_picker_enabled && !yes_no_enabled) {
        setValue('age_gate.enabled', false, {
          shouldDirty: true,
        });
        setValue('age_gate.date_picker.store_ids', [], {
          shouldDirty: true,
        });
        setValue('age_gate.yes_no.store_ids', [], {
          shouldDirty: true,
        });
        setValue('age_gate.default', null, { shouldDirty: true });
      } else if (date_picker_enabled && !yes_no_enabled) {
        setValue('age_gate.enabled', true, {
          shouldDirty: true,
        });
        setValue('age_gate.default', 'date_picker', { shouldDirty: true });
        setValue('age_gate.yes_no.store_ids', [], {
          shouldDirty: true,
        });
        setValue('age_gate.date_picker.store_ids', allStoreIds, {
          shouldDirty: true,
        });
      } else if (!date_picker_enabled && yes_no_enabled) {
        setValue('age_gate.enabled', true, {
          shouldDirty: true,
        });
        setValue('age_gate.default', 'yes_no', { shouldDirty: true });
        setValue('age_gate.date_picker.store_ids', [], {
          shouldDirty: true,
        });
        setValue('age_gate.yes_no.store_ids', allStoreIds, {
          shouldDirty: true,
        });
      }
    }
  }, [allStoreIds, date_picker_enabled, isDirty, setValue, yes_no_enabled]);

  useEffect(() => {
    register('age_gate.date_picker.store_ids');
    register('age_gate.yes_no.store_ids');
  }, [register]);

  const publish: SubmitHandler<FieldValues> = useCallback(
    (data) => {
      updateBloomMenu(
        {
          theme: {
            ...bloomMenuQuery.data?.theme.bloom,
            age_gate: data['age_gate'],
          },
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Menu updated!',
              variant: 'success',
            });
            reset({
              age_gate: data['age_gate'],
            });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [updateBloomMenu, bloomMenuQuery, toast, reset, navigate]
  );

  const preview = useCallback(() => {
    setPreviewLoading(true);
    const data = getValues();
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuQuery.data?.theme.bloom,
          age_gate: data['age_gate'],
        },
      },
      {
        onError: () => {
          setPreviewLoading(false);
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          setPreviewLoading(false);
          if (!bloomMenuQuery.data?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuQuery.data.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          previewUrl.searchParams.set('age-gate', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [getValues, updateBloomMenu, bloomMenuQuery.data, toast]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading && !previewLoading}
      onSubmit={publish}
      preview={{
        onClick: preview,
        disable: isLoading,
        loading: isLoading && previewLoading,
      }}
      title="Age gate"
    >
      <Flex>
        <StyledTabs value={selectedTab}>
          <StyledTab
            label="Yes/No selection"
            selected={selectedTab === 'yes_no'}
            value="yes_no"
            onClick={() => setSelectedTab('yes_no')}
          />
          <StyledTab
            label="Date Selection"
            selected={selectedTab === 'date_picker'}
            value="date_picker"
            onClick={() => setSelectedTab('date_picker')}
          />
        </StyledTabs>
      </Flex>
      <Flex justifyContent="center">
        <Flex flexDirection="column" m={32} maxWidth={880}>
          <Typography>
            In the event your customer navigates to the Bloom menu directly and
            bypasses your main website, we want to make sure we're still
            compliant and display an age gap pop up. The settings below gives
            you the ability to customize the age gate pop up further.
          </Typography>

          <AgeGateFormContainer>
            <Flex justifyContent="space-between">
              <Controller
                name={
                  isYesNoSelected
                    ? 'age_gate.yes_no.enabled'
                    : 'age_gate.date_picker.enabled'
                }
                control={control}
                render={({ field }) => (
                  <Flex flexDirection="row" gap={12} mb={24}>
                    <SwitchField
                      label="Toggle age gate type"
                      name={
                        isYesNoSelected
                          ? 'age_gate.yes_no.enabled'
                          : 'age_gate.date_picker.enabled'
                      }
                      onChange={(toggle) => field.onChange(toggle)}
                      labelHidden
                      checked={!!field.value}
                    />
                    <Typography variant="body-bold">Enabled</Typography>
                  </Flex>
                )}
              />
              <Controller
                name="age_gate.default"
                control={control}
                render={({ field }) => {
                  return (
                    <Flex flexDirection="row" gap={12} mb={24}>
                      <SwitchField
                        label="Set default age gate type"
                        name="age_gate.default"
                        onChange={() => {
                          if (isYesNoSelected) {
                            field.onChange('yes_no');
                            setValue('age_gate.date_picker.store_ids', [], {
                              shouldDirty: true,
                            });
                            setValue('age_gate.yes_no.store_ids', allStoreIds, {
                              shouldDirty: true,
                            });
                          } else {
                            field.onChange('date_picker');
                            setValue('age_gate.yes_no.store_ids', [], {
                              shouldDirty: true,
                            });
                            setValue(
                              'age_gate.date_picker.store_ids',
                              allStoreIds,
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                        labelHidden
                        disabled={!(date_picker_enabled && yes_no_enabled)}
                        checked={selectedTab === field.value}
                      />
                      <Typography variant="body-bold">
                        Set as default
                      </Typography>
                    </Flex>
                  );
                }}
              />
            </Flex>
            <Controller
              name={
                isYesNoSelected
                  ? 'age_gate.yes_no.message'
                  : 'age_gate.date_picker.message'
              }
              control={control}
              rules={{ required: true, pattern: /\S+/ }}
              render={({ field }) => (
                <Flex flexDirection="column" gap={12}>
                  <Typography variant="body-bold">Text</Typography>
                  <FormInput
                    type="text"
                    onChange={(message) => field.onChange(message)}
                    value={field.value || ''}
                  />
                  <Flex justifyContent="flex-end">
                    <Link
                      onClick={() =>
                        isYesNoSelected
                          ? field.onChange(
                              defaultValues.age_gate.yes_no.message
                            )
                          : field.onChange(
                              defaultValues.age_gate.date_picker.message
                            )
                      }
                    >
                      Revert to default text
                    </Link>
                  </Flex>
                </Flex>
              )}
            />
            {isYesNoSelected && (
              <Controller
                name="age_gate.yes_no.button_order"
                control={control}
                render={({ field }) => (
                  <Flex flexDirection="column" gap={12} mt={24}>
                    <Typography variant="body-bold">Button order</Typography>
                    <Typography color="grays-mid">
                      Choose the order in which the No and Yes buttons are
                      displayed to customers.
                    </Typography>
                    <RadioFieldGroup
                      name="button_order"
                      options={[
                        {
                          id: 'button_order_no_yes',
                          label: 'No / Yes',
                          value: 'no_yes',
                        },
                        {
                          id: 'button_order_yes_no',
                          label: 'Yes / No',
                          value: 'yes_no',
                        },
                      ]}
                      row
                      externalValue={field.value ?? 'no_yes'}
                      onChange={(value) => field.onChange(value)}
                    />
                  </Flex>
                )}
              />
            )}
            <Controller
              name={
                isYesNoSelected
                  ? 'age_gate.yes_no.cookie_name'
                  : 'age_gate.date_picker.cookie_name'
              }
              control={control}
              render={({ field }) => (
                <Flex flexDirection="column" gap={12} mt={24}>
                  <Typography variant="body-bold">
                    Cookie name (Optional)
                  </Typography>
                  <FormInput
                    type="text"
                    onChange={(message) => field.onChange(message)}
                    value={field.value ?? ''}
                  />
                  <Typography color="grays-mid">
                    To locate your cookie name, access your age gate plugin's
                    settings and paste the name here. If you aren't using a
                    plugin, you will need to work with a technical engineer to
                    retrieve the cookie name with your browser's developer
                    tools.
                  </Typography>
                </Flex>
              )}
            />
          </AgeGateFormContainer>

          {showStoreSection && (
            <AgeGateStoreSection
              stores={bloomMenuQuery.data?.stores || []}
              selectedTab={selectedTab}
              setValue={setValue}
              currentDatePickerStores={date_picker_stores}
              currentYesNoStores={yes_no_stores}
            />
          )}
        </Flex>
      </Flex>
    </ModalPageWrapper>
  );
};
